import { Dropdown } from 'antd';
import cls from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState } from 'react';

import { useExposeItem } from '../../../hooks';
import { setToastInfo, setToastStatus, setToastVisible, useAppDispatch } from '../../../state';
import { track } from '../../../track';
import { SubmitFailedReason, SubmitWorkspaceDraftResp, WorkspaceNpcInfo } from '../../../types';
import { EWorkspaceNpcStatus } from '../../../types/npc';
import { LocalImage } from '../../Image';
import { trackWithNpc } from '../NpcTabs';

const PAGE = 'ugc_main_page';

interface INpcCardProps {
  styles: Record<string, string>;
  npcInfo: WorkspaceNpcInfo;
  topRightIcon: React.ReactNode;
  dropdownMenu: React.ReactNode;
  statusTab: React.ReactNode;
  status: EWorkspaceNpcStatus;
  defaultImg: string;
}

export function NpcCard({
  styles,
  npcInfo,
  dropdownMenu,
  topRightIcon,
  statusTab,
  status,
  defaultImg,
}: INpcCardProps) {
  const [isHover, setIsHover] = useState<boolean>(false);
  const ref = useRef<any>(null);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const handleOpenNotification = useCallback(
    (status: 'success' | 'warning' | 'error', toastInfo: SubmitWorkspaceDraftResp) => {
      dispatch(setToastVisible(true));
      dispatch(setToastStatus(status));
      dispatch(setToastInfo(toastInfo));
    },
    [dispatch]
  );

  const handleClickNpc = useCallback(() => {
    trackWithNpc(npcInfo, 'ugc_npc_click', PAGE);
    if (status === EWorkspaceNpcStatus.UnderReview) {
      handleOpenNotification('warning', {
        fail_message: t('under_moderation_not_editable'),
        fail_reason: SubmitFailedReason.UnderModeration,
      });
    } else {
      location.href = '/create/edit?npc_id=' + npcInfo.npc_id;
    }
  }, [npcInfo, status]);

  const onExpose = useCallback(() => {
    trackWithNpc(npcInfo, 'ugc_npc_view', PAGE, {}, 'collect');
  }, [npcInfo, status]);

  useExposeItem({
    key: npcInfo.npc_id,
    ref,
    onExpose,
  });

  return (
    <div
      className={styles.cardWrapper}
      onMouseEnter={() => {
        setIsHover(true);
      }}
      onMouseLeave={() => {
        setIsHover(false);
      }}
      onClick={handleClickNpc}
    >
      <div ref={ref} className={styles.imageWrapper}>
        <LocalImage
          alt="npc image"
          className={cls(styles.image, { [styles.image_hover]: isHover })}
          src={npcInfo.img_info.base_img_url || defaultImg}
          height={240}
          width={240}
        />
        {npcInfo.status_info.is_demo && <div className={styles.demo}>{t('demoText')}</div>}
        {isHover && (
          <Dropdown
            placement="bottomRight"
            dropdownRender={() => dropdownMenu}
            getPopupContainer={() => ref.current}
          >
            {topRightIcon}
          </Dropdown>
        )}
      </div>
      <div className={styles.info}>
        <div className={styles.title}>
          {!!npcInfo.setting_info.npc_name && (
            <div className={styles.npc_name}>{npcInfo.setting_info.npc_name}</div>
          )}
          {statusTab}
        </div>
        <div className={styles.desc}>
          <p>{npcInfo.setting_info.desc}</p>
        </div>
        <div className={styles.divider} />
        <div className={styles.time}>
          <p>{`${t('last_update_time')} ${dayjs(npcInfo.status_info.update_timestamp).format('MM-DD HH:mm')}`}</p>
        </div>
      </div>
    </div>
  );
}
